/*
 *  Copyright © 2025 Cenozon Inc. All Rights Reserved.
 */

import { ROUTE_SEARCH_ACTION_TYPES } from '../action/routeInspectionSearchAction';
import { ROUTE_SEARCH_CONSTANTS } from '../../screens/routeinspectionsearch/routeInspectionSearchConstants';

// Initial State
const initialState = {
  // Search Type
  searchType: ROUTE_SEARCH_CONSTANTS.SEARCH_TYPE.ROUTE,
  
  // Data Retention Flag - similar to main search page
  retainData: false,
  
  // Form Selections State - moved from component local state to Redux
  formSelections: {
    selectedRoutes: [],
    selectedOperationalAreas: [],
    selectedVendors: [],
    selectedProjectNames: [],
    selectedFromLocation: [],
    selectedToLocation: [],
    selectedToolType: []
  },
  
  // Pagination State - moved from component local state to Redux
  paginationState: {
    routePage: 0,
    routeRowsPerPage: 10,
    routeTotalCount: 0,
    page: 0,
    rowsPerPage: 10,
    totalCount: 0
  },
  
  // Form State Persistence
  savedFormState: {},
  
  // Route Search State
  routeSearch: {
    loading: false,
    data: {
      routeId: '',
      routeName: '',
      operationalArea: '',
      client: '',
      status: ''
    },
    results: [],
    error: null,
    hasSearched: false
  },
  
  // Inspection Search State
  inspectionSearch: {
    loading: false,
    data: {
      inspectionId: '',
      routeName: '',
      inspectionDate: '',
      inspectionType: '',
      vendor: '',
      status: ''
    },
    results: [],
    error: null,
    hasSearched: false
  }
};

// Reducer
const routeInspectionSearchReducer = (state = initialState, action) => {
  switch (action.type) {
    // Search Type Actions
    case ROUTE_SEARCH_ACTION_TYPES.SET_SEARCH_TYPE:
      return {
        ...state,
        searchType: action.payload,
        // Clear previous results when switching search type
        routeSearch: {
          ...state.routeSearch,
          hasSearched: false,
          results: []
        },
        inspectionSearch: {
          ...state.inspectionSearch,
          hasSearched: false,
          results: []
        }
      };

    // Route Search Actions
    case ROUTE_SEARCH_ACTION_TYPES.ROUTE_SEARCH_REQUEST:
      return {
        ...state,
        routeSearch: {
          ...state.routeSearch,
          loading: true,
          error: null
        }
      };

    case ROUTE_SEARCH_ACTION_TYPES.ROUTE_SEARCH_SUCCESS:
      return {
        ...state,
        routeSearch: {
          ...state.routeSearch,
          loading: false,
          results: action.payload?.results || action.payload || [],
          hasSearched: true,
          error: null
        }
      };

    case ROUTE_SEARCH_ACTION_TYPES.ROUTE_SEARCH_FAILURE:
      return {
        ...state,
        routeSearch: {
          ...state.routeSearch,
          loading: false,
          error: action.payload,
          results: [],
          hasSearched: true
        }
      };

    case ROUTE_SEARCH_ACTION_TYPES.UPDATE_ROUTE_SEARCH_DATA:
      return {
        ...state,
        routeSearch: {
          ...state.routeSearch,
          data: {
            ...state.routeSearch.data,
            ...action.payload
          }
        }
      };

    case ROUTE_SEARCH_ACTION_TYPES.CLEAR_ROUTE_SEARCH:
      return {
        ...state,
        routeSearch: {
          ...state.routeSearch,
          data: {
            routeId: '',
            routeName: '',
            operationalArea: '',
            client: '',
            status: ''
          },
          results: [],
          hasSearched: false,
          error: null
        }
      };

    // Inspection Search Actions
    case ROUTE_SEARCH_ACTION_TYPES.INSPECTION_SEARCH_REQUEST:
      return {
        ...state,
        inspectionSearch: {
          ...state.inspectionSearch,
          loading: true,
          error: null
        }
      };

    case ROUTE_SEARCH_ACTION_TYPES.INSPECTION_SEARCH_SUCCESS:
      return {
        ...state,
        inspectionSearch: {
          ...state.inspectionSearch,
          loading: false,
          results: action.payload || {},
          hasSearched: true,
          error: null
        }
      };

    case ROUTE_SEARCH_ACTION_TYPES.INSPECTION_SEARCH_FAILURE:
      return {
        ...state,
        inspectionSearch: {
          ...state.inspectionSearch,
          loading: false,
          error: action.payload,
          results: [],
          hasSearched: true
        }
      };

    case ROUTE_SEARCH_ACTION_TYPES.UPDATE_INSPECTION_SEARCH_DATA:
      return {
        ...state,
        inspectionSearch: {
          ...state.inspectionSearch,
          data: {
            ...state.inspectionSearch.data,
            ...action.payload
          }
        }
      };

    case ROUTE_SEARCH_ACTION_TYPES.UPDATE_FORM_SELECTIONS:
      return {
        ...state,
        formSelections: {
          ...state.formSelections,
          ...action.payload
        }
      };

    case ROUTE_SEARCH_ACTION_TYPES.UPDATE_PAGINATION_STATE:
      return {
        ...state,
        paginationState: {
          ...state.paginationState,
          ...action.payload
        }
      };

    case ROUTE_SEARCH_ACTION_TYPES.CLEAR_INSPECTION_SEARCH:
      return {
        ...state,
        inspectionSearch: {
          ...state.inspectionSearch,
          data: {
            inspectionId: '',
            routeName: '',
            inspectionDate: '',
            inspectionType: '',
            vendor: '',
            status: ''
          },
          results: [],
          hasSearched: false,
          error: null
        }
      };

    // Reset Actions
    case ROUTE_SEARCH_ACTION_TYPES.RESET_ROUTE_INSPECTION_SEARCH:
      return initialState;

    // State Persistence Actions
    case ROUTE_SEARCH_ACTION_TYPES.SAVE_FORM_STATE:
      return {
        ...state,
        savedFormState: action.payload
      };

    case ROUTE_SEARCH_ACTION_TYPES.RESTORE_FORM_STATE:
      return {
        ...state,
        // Keep the saved state for future use
        savedFormState: state.savedFormState
      };

    case ROUTE_SEARCH_ACTION_TYPES.CLEAR_FORM_STATE:
      return {
        ...state,
        savedFormState: null
      };

    case ROUTE_SEARCH_ACTION_TYPES.RETAIN_SEARCH_DATA:
      return {
        ...state,
        retainData: action.payload
      };

    default:
      return state;
  }
};

export default routeInspectionSearchReducer;
